/**
 * Firebase Configuration
 * 
 * Initializes Firebase for GCIP authentication.
 * Uses environment-specific configuration for nonprod/prod.
 * 
 * @module config/firebase
 */

import { initializeApp, FirebaseApp } from 'firebase/app';
import { getAuth, Auth, connectAuthEmulator } from 'firebase/auth';

/**
 * Firebase configuration for web client
 * 
 * Environment-specific values:
 * - Nonprod: lore-health-test-dc35d
 * - Prod: lore-health
 */
function getFirebaseConfig() {
  const env = process.env.REACT_APP_ENV || 'development';
  const isProd = env === 'production' || env === 'prod';
  
  if (isProd) {
    return {
      apiKey: process.env.REACT_APP_FIREBASE_API_KEY_PROD || process.env.REACT_APP_FIREBASE_API_KEY,
      authDomain: 'lore-health.firebaseapp.com',
      projectId: 'lore-health',
      storageBucket: 'lore-health.firebasestorage.app',
    };
  }
  
  // Nonprod/Development configuration
  return {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY || 'AIzaSyCkOmSi8hmhz5nxhuZ2zDZcTmiFkUat8gU',
    authDomain: 'lore-health-test-dc35d.firebaseapp.com',
    projectId: 'lore-health-test-dc35d',
    storageBucket: 'lore-health-test-dc35d.firebasestorage.app',
  };
}

/**
 * Get tenant ID for multi-tenant GCIP authentication
 */
export function getTenantId(): string {
  const env = process.env.REACT_APP_ENV || 'development';
  const isProd = env === 'production' || env === 'prod';
  
  return isProd ? 'players-prod-cwbdk' : 'players-nonprod-l1fx3';
}

// Initialize Firebase app
let app: FirebaseApp | null = null;
let auth: Auth | null = null;

/**
 * Get initialized Firebase app instance
 */
export function getFirebaseApp(): FirebaseApp {
  if (!app) {
    const config = getFirebaseConfig();
    app = initializeApp(config);
  }
  return app;
}

/**
 * Get initialized Firebase Auth instance with tenant ID configured
 */
export function getFirebaseAuth(): Auth {
  if (!auth) {
    const firebaseApp = getFirebaseApp();
    auth = getAuth(firebaseApp);
    
    // Configure tenant ID for multi-tenant GCIP
    const tenantId = getTenantId();
    auth.tenantId = tenantId;

    // Connect to emulator in development if configured
    if (process.env.REACT_APP_USE_FIREBASE_EMULATOR === 'true') {
      connectAuthEmulator(auth, 'http://localhost:9099');
    }
  }
  return auth;
}

// Export types for convenience
export type { FirebaseApp, Auth };

